/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { LegendColor, LegendItem } from 'src/luis/projects/analytics/components/TrainingEvaluation/Legend/LegendItem';
import {
    ComparisonItem,
    LegendLabel,
    Row,
    StatsText
} from 'src/luis/projects/analytics/components/TrainingEvaluation/Legend/LegendRowHelpers';

export type PredictionsLegendRowProps = {
    /**
     * The legend color.
     */
    color: LegendColor;

    /**
     * The accuracy.
     */
    percent: string;

    /**
     * The compared difference.
     */
    percentDiff: number;

    /**
     * Indicates if comparison is being loaded.
     */
    loadingDiff: boolean;

    /**
     * The displayed label.
     */
    label: string;

    /**
     * A description illustrates the meaning of the given label.
     */
    description: string;

    /**
     * The type of comparison either the increasing the
     * difference would be good thing or not.
     */
    comparisonType?: boolean;
};

const legendTheme = getScopedTheme('Dashboard');

/**
 * The legend row to represent percentages like:
 * correct predictions, incorrect predictions, unclear predictions ...etc.
 *
 * @param props the properties of the row.
 */
export const PredictionsLegendRow = (props: PredictionsLegendRowProps) => {
    const { color, label, percent, description, loadingDiff, percentDiff, comparisonType } = props;

    return (
        <Row>
            <LegendItem color={color} />

            <StatsText customProps={{ bold: true, font: legendTheme.largeFontSize, lineHeight: legendTheme.xLargeLineHeight }}>
                {percent}
            </StatsText>

            <ComparisonItem customProps={{ rowType: 'predictions', isBadResult: comparisonType || percentDiff < 0 }}>
                {loadingDiff ? (percentDiff !== null ? `${percentDiff < 0 ? '' : '+'}${percentDiff.toFixed(1)}%` : '--') : ''}
            </ComparisonItem>

            <LegendLabel>
                {label}
                <CalloutButton content={description} />
            </LegendLabel>
        </Row>
    );
};
